import {Auth, API} from 'aws-amplify';
import {createSlice} from '@reduxjs/toolkit';
import {v4 as uuidv4} from 'uuid';

import {ENDPOINT} from '../../constants';

function startLoading(state) {
  state.isLoading = true;
}

function loadingFailed(state, action) {
  state.isLoading = false;
  state.error = action.payload;
}

export const userSlice = createSlice({
  name: 'user',
  initialState: {
    isLoading: false,
    user: {}, // authenticated user
    users: {}, // other users
    userGroups: {},
  },
  reducers: {
    getUsersStart: startLoading,
    getUsersSuccess: (state, action) => {
      action.payload.forEach((user) => (state.users[user._id] = user));
      state.isLoading = false;
      state.error = null;
    },
    getUsersFailure: loadingFailed,
    getUserStart: startLoading,
    getUserSuccess: (state, action) => {
      state.user = action.payload;
      state.users[action.payload._id] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    getUserFailure: loadingFailed,
    updateUserStart: startLoading,
    updateUserSuccess: (state, action) => {
      const user = action.payload;
      if (user?.avatarURL) user.avatarURL += `?cacheBust=${uuidv4()}`;
      state.user = user;
      state.users[user._id] = user;
      state.isLoading = false;
      state.error = null;
    },
    updateUserFailure: loadingFailed,
    getUserGroupStart: startLoading,
    getUserGroupSuccess: (state, action) => {
      state.userGroups[action.payload._id] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    getUserGroupFailure: loadingFailed,
  },
});

export const {
  getUsersStart,
  getUsersSuccess,
  getUsersFailure,
  getUserStart,
  getUserSuccess,
  getUserFailure,
  updateUserStart,
  updateUserSuccess,
  updateUserFailure,
  getUserGroupStart,
  getUserGroupSuccess,
  getUserGroupFailure,
} = userSlice.actions;

export const fetchUsers = (userIDs) => async (dispatch) => {
  try {
    dispatch(getUsersStart());
    const user = await API.get(ENDPOINT, `/users?userIDs=${JSON.stringify(userIDs)}`);
    dispatch(getUsersSuccess(user));
  } catch (error) {
    dispatch(getUsersFailure(error.toString()));
  }
};

// Fetch authenticated user profile from MongoDB
export const fetchUser = () => async (dispatch) => {
  try {
    dispatch(getUserStart());
    const {username: userID} = await Auth.currentAuthenticatedUser();
    const user = await API.get(ENDPOINT, `/users/${userID}`);
    dispatch(getUserSuccess(user));
    return user;
  } catch (error) {
    dispatch(getUserFailure(error.toString()));
  }
};

// Update authenticated user profile in MongoDB
export const updateUser = (partialUser) => async (dispatch) => {
  try {
    dispatch(updateUserStart());
    const {username: userID} = await Auth.currentAuthenticatedUser();
    const request = {body: partialUser};
    const user = await API.patch(ENDPOINT, `/users/${userID}`, request);
    dispatch(updateUserSuccess(user));
  } catch (error) {
    dispatch(updateUserFailure(error.toString()));
  }
};

// Fetch user group
export const fetchUserGroup = (userGroupID) => async (dispatch) => {
  try {
    dispatch(getUserGroupStart());
    const userGroup = await API.get(ENDPOINT, `/user-groups/${userGroupID}`);
    dispatch(getUserGroupSuccess(userGroup));
  } catch (error) {
    dispatch(getUserGroupFailure(error.toString()));
  }
};

export const selectIsLoading = (state) => state.user.isLoading;
export const selectUsers = (state) => state.user.users;
export const selectUser = (state) => state.user.user;
export const selectUserGroup = (state) => state.user.userGroups[state.user.user.userGroupID]; // authenticated user's user group
export const selectUserGroups = (state) => state.user.userGroups;

export default userSlice.reducer;
